/**
 * App mode resolution (CCA vs EIA).
 *
 * This is the single source of truth for "which product am I?". CCA and EIA ship
 * from ONE codebase; the mode is decided at runtime from the URL host/port (or an
 * explicit env override for local dev). Everything variant-specific keys off this.
 *
 * Resolution order:
 *   1. VITE_APP_MODE env override (local dev convenience), if set.
 *   2. Host/port match from variants.config.js.
 *   3. No match: EIA on a deployed host (legacy's `isCCA() ? CCA : EIA`), CCA
 *      locally — with a console warning.
 */
import { env } from '@config/env';
import { isLocalHost } from '@utils/hosts';

import { VARIANTS, matchVariantByLocation } from './variants.config';

export const APP_MODES = Object.freeze({ CCA: 'cca', EIA: 'eia' });

let cachedMode = null;

/**
 * Resolve the app mode. Pure w.r.t. a given location, memoized for the real
 * window.location so repeated calls are cheap.
 * @param {Location | {hostname:string, port:string}} [location]
 * @returns {'cca' | 'eia'}
 */
export function resolveAppMode(
  location = typeof window !== 'undefined' ? window.location : undefined,
) {
  if (env.appModeOverride) return env.appModeOverride;

  if (!location) return APP_MODES.CCA;

  // Only memoize for the live browser location, not for arbitrary test inputs.
  const isLive = typeof window !== 'undefined' && location === window.location;
  if (isLive && cachedMode) return cachedMode;

  // VITE_CCA_EXTRA_HOSTS adds deploy-time CCA hosts (e.g. a server reached by
  // IP address) without writing them in source.
  const matched = matchVariantByLocation(location, { cca: env.ccaExtraHosts });
  // Unmatched: on a deployed host, EIA — legacy decides every product question
  // with `isCCA() ? 'CCA' : 'EIA'` (App.vue:92, apiservice.js:12), so a host not
  // recognised as CCA is EIA there. Locally, CCA (no legacy behaviour to keep).
  const mode = matched ?? (isLocalHost(location.hostname) ? APP_MODES.CCA : APP_MODES.EIA);

  if (!matched && typeof console !== 'undefined') {
    // eslint-disable-next-line no-console
    console.warn(
      `[variant] Could not match ${location.hostname}:${location.port} to a known ` +
        `variant; defaulting to '${mode}'. See src/variant/variants.config.js.`,
    );
  }

  if (isLive) cachedMode = mode;
  return mode;
}

/** The resolved variant descriptor (label, theme class, etc.). */
export function getVariant(mode = resolveAppMode()) {
  return VARIANTS[mode] ?? VARIANTS[APP_MODES.CCA];
}

/** Test seam: clear the memoized mode. */
export function __resetAppModeCache() {
  cachedMode = null;
}
